import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

import { MONTHS, DAYS, SelectOption } from 'src/app/constants/date.constant';

/**
 * This component will take up the full size of the screen. Provide a container to control sizing.
 *
 * `DateOfBirth` is a hidden {FormControl} that is used to send the DOB value to the API.
 * Use `DateOfBirthYear`, `DateOfBirthMonth`, and `DateOfBirthDay` {FormControl}s to access the individual values.
 *
 * When setting the values make sure to set the year, month, and day in that order.
 *
 * Contains the following {FormControl}s:
 * * SSN
 * * DateOfBirthYear
 * * DateOfBirthMonth
 * * DateOfBirthDay
 * * DateOfBirth
 */
@Component({
  selector: 'app-secure-info',
  templateUrl: './secure-info.component.html',
  styleUrls: ['./secure-info.component.scss'],
})
export class SecureInfoComponent implements OnInit, OnDestroy {
  @Input() controls: {
    [key: string]: AbstractControl;
  } = {};

  /**
   * Determines if the {FormControl}s are for the co-applicant and sends the
   * change event to the parent component.
   */
  @Input() coApplicant = false;

  /**
   * Number of times these {FormControl}s have been used on same page. Defaults to `1`.
   * Use this to create unique IDs for each instance of this component.
   */
  @Input() count = 1;

  showPassword = false;

  unfilteredMonths: Array<SelectOption> = MONTHS;
  months: Array<SelectOption> = MONTHS;
  unfilteredDays: Array<SelectOption> = DAYS;
  days: Array<SelectOption> = DAYS;
  years: Array<SelectOption> = [];

  private subscriptions: Array<Subscription> = [];

  constructor(private ngbDateParserService: NgbDateParserFormatter) {}

  ngOnInit(): void {
    this.years = this.getYears();

    this.subscriptions.push(
      this.controls['DateOfBirthYear'].valueChanges.subscribe((year) => {
        this.onYearChange(year);
      })
    );

    this.subscriptions.push(
      this.controls['DateOfBirthMonth'].valueChanges.subscribe((month) => {
        this.onMonthChange(month);
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  getFormControl(key: string): FormControl {
    return this.controls[key] as FormControl;
  }

  /**
   * Sets the max date to 18 years from the current date and displays in descending order.
   *
   * @returns Array of {SelectOption} for the year selector.
   */
  getYears(): Array<SelectOption> {
    const years: Array<SelectOption> = [];
    const currentYear = new Date().getFullYear() - 18;

    for (let i = currentYear; i >= currentYear - 100; i--) {
      years.push({ value: `${i}`, text: `${i}` });
    }

    return years;
  }

  /**
   * @deprecated
   *
   * Sets the max date to 18 years from the current date. This also fixes an issue
   * where the year selector was limited to +/-10 years.
   *
   * "If you don't specify any of them, you'll have infinite navigation and the year
   * select box will display [-10, +10] years from currently visible month."
   *
   *
   * @returns Max date of type {NgbStruct} for NgbDatepicker.
   */
  getMaxDateForDOBControl(): NgbDateStruct {
    return this.ngbDateParserService.parse(
      moment().subtract(18, 'years').format('YYYY-MM-DD')
    )!;
  }

  /**
   * @deprecated
   */
  getDOBFormControlValue(key: string): NgbDateStruct {
    if (this.controls[key] && this.controls[key].value) {
      return this.controls[key].value;
    }

    // By default will return today's date without a selection.
    return this.ngbDateParserService.parse(moment().format('YYYY-MM'))!;
  }

  controlIsRequired(key: string): boolean {
    if (
      this.controls[key] &&
      this.controls[key].hasValidator(Validators.required)
    ) {
      return true;
    }

    return false;
  }

  onYearChange(value: string) {
    const year = value;
    const today = new Date();
    const eighteen = today.getFullYear() - 18;

    // Reset the month and day to prevent invalid dates.
    this.getFormControl('DateOfBirthMonth').setValue('');
    this.getFormControl('DateOfBirthDay').setValue('');

    // Check if the year is 18 years ago.
    if (parseInt(year) === eighteen) {
      const currentMonth = today.getMonth() + 1;

      this.months = this.unfilteredMonths.filter((month) => {
        return parseInt(month.value) <= currentMonth;
      });

      return;
    }

    this.months = this.unfilteredMonths;
  }

  /**
   * Get month and year from event so the form can set the max day for the month.
   */
  onMonthChange(value: string) {
    // Get month and year from event.
    const month = value;
    const year = this.getFormControl('DateOfBirthYear').value;
    const today = new Date();
    const eighteen = today.getFullYear() - 18;

    // Reset the day to prevent invalid dates.
    this.getFormControl('DateOfBirthDay').setValue('');

    const lastDayOfMonth = moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();

    if (parseInt(year) === eighteen) {
      const currentMonth = today.getMonth() + 1;

      if (parseInt(month) === currentMonth) {
        const currentDay = today.getDate();

        this.days = this.unfilteredDays.filter((day) => {
          return parseInt(day.value) <= currentDay;
        });

        return;
      }
    }

    this.days = this.unfilteredDays.filter((day) => {
      return parseInt(day.value) <= lastDayOfMonth;
    });
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }
}
